import { getDeepLinkScheme } from '@/utils/appConfiguration';
import {
  describeErrorForLog,
  describeUrlForLog,
} from '@/utils/logger/describe-url-for-log';
import { logger } from '@/utils/logger/logger';
import { getEnvironment } from '@heidi/api/config';
import { isTauri } from '@heidi/runtime/platform';
import { downloadDir, join } from '@tauri-apps/api/path';
import { save } from '@tauri-apps/plugin-dialog';
import { BaseDirectory, exists, writeFile } from '@tauri-apps/plugin-fs';
import { openPath, openUrl } from '@tauri-apps/plugin-opener';

export const tauriShouldUseTranslations = () => {
  return false;
};

// Compatibility re-export (Wave 1 · P1). `isTauri` now lives in
// `@heidi/runtime/platform`; this runtime re-export keeps the existing
// `@/tauri/utils` importers (and their `jest.mock` sites) compiling unchanged.
// The dev-time NEXT_PUBLIC_IS_TAURI validation that used to run inside this
// function now runs in `configurePlatform()` at app bootstrap
// (src/utils/runtime-wiring.ts). Burned down by the deferred consumer codemod.
export { isTauri };

/**
 * The single funnel for opening a url in the OS browser on desktop. The
 * rejection is handled here rather than at the 22 call sites: an opener scope
 * rejection embeds the whole url in its message, so a bare `void` at the call
 * site puts a Stripe billing-portal url — path, query and all — into the
 * unhandled-rejection report, which is the one place the redaction below
 * cannot reach.
 *
 * Returns `undefined` on web, which `openUrlInTauri(href)?.catch` relies on.
 */
export const openUrlInTauri = (url: string) => {
  if (!isTauri()) return;

  return openUrl(url).catch((err: unknown) => {
    logger.error('[tauri] Failed to open URL in system browser', {
      url: describeUrlForLog(url),
      error: describeErrorForLog(err),
    });
  });
};

export const isUsingReactKinde = () => {
  return isTauri();
};

export const tauriStripeRedirectUrl = () => {
  return (
    `https://scribe.heidihealth.com/stripe-redirect.html?redirect=${tauriDeepLinkPath()}` +
    'stripe-redirect'
  );
};

export const tauriDeepLinkPath = () => `${getDeepLinkScheme(getEnvironment())}://`;

export async function saveBlobToDisk(blobFile: Blob, filename: string): Promise<boolean> {
  const filePath = await save({
    defaultPath: filename,
    filters: [{ name: 'File', extensions: [filename.split('.').pop() || 'dat'] }],
  });
  if (!filePath) return false;

  const arrayBuffer = await blobFile.arrayBuffer();
  await writeFile(filePath, new Uint8Array(arrayBuffer));
  return true;
}

/**
 * Write a Blob straight into the OS Downloads folder without prompting the
 * user. Requires the `fs:scope-download` permission on the calling window's
 * capability. Appends " (n)" to the filename when it already exists,
 * mirroring browser download behaviour instead of overwriting.
 *
 * @returns the absolute path the file was saved to.
 */
export async function saveBlobToDownloads(
  blobFile: Blob,
  filename: string
): Promise<string> {
  const dotIndex = filename.lastIndexOf('.');
  const stem = dotIndex > 0 ? filename.slice(0, dotIndex) : filename;
  const extension = dotIndex > 0 ? filename.slice(dotIndex) : '';

  let candidate = filename;
  let suffix = 1;
  while (await exists(candidate, { baseDir: BaseDirectory.Download })) {
    candidate = `${stem} (${suffix})${extension}`;
    suffix += 1;
  }

  const arrayBuffer = await blobFile.arrayBuffer();
  await writeFile(candidate, new Uint8Array(arrayBuffer), {
    baseDir: BaseDirectory.Download,
  });
  return join(await downloadDir(), candidate);
}

/**
 * Open a file with the OS default application (best-effort). The path must be
 * covered by an `opener:allow-open-path` scope in the window's capability.
 */
export async function openPathInDefaultApp(path: string): Promise<void> {
  await openPath(path);
}
